import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { AvatarPicker } from '@/components/user/AvatarPicker';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateUserProfile } from '@/hooks/useUpdateUserProfile';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Loader2, FileUser, ChevronRight } from 'lucide-react';
import { SafeNavLink } from '@/components/system/SafeNavLink';

export default function UserProfileSettings() {
  const { user } = useAuth();
  const updateProfile = useUpdateUserProfile();
  const hrEnabled = useGlobalFeatureFlag('ENABLE_HR_MODULE');

  const [fullName, setFullName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Fetch user profile
  const { data: profile, isLoading } = useAuthQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, avatar_url, created_at, updated_at')
        .eq('id', user?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setAvatarUrl(profile.avatar_url || null);
    }
  }, [profile]);

  const handleSave = () => {
    if (!user?.id) return;

    updateProfile.mutate({
      userId: user.id,
      fullName: fullName || undefined,
      avatarUrl: avatarUrl || undefined,
    });
  };

  const hasChanges = 
    fullName !== (profile?.full_name || '') ||
    avatarUrl !== (profile?.avatar_url || null);

  if (isLoading) {
    return (
      <div className="container max-w-2xl py-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-2xl py-8">
      <div className="mb-6">
        <SafeNavLink to="/">
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </SafeNavLink>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Profile Settings</CardTitle>
          <CardDescription>
            Manage your personal information and avatar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar Section */}
          <div className="space-y-2">
            <Label>Profile Picture</Label>
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                <AvatarImage src={avatarUrl || ''} />
                <AvatarFallback className="text-lg">
                  {fullName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <AvatarPicker 
                currentUrl={avatarUrl}
                onSelect={setAvatarUrl}
                userName={fullName || user?.email || 'User'}
              />
            </div>
          </div>

          {/* Name Section */}
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input 
              id="fullName"
              value={fullName} 
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>

          {/* Email Section (readonly) */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email"
              value={user?.email || ''} 
              disabled 
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Email cannot be changed
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave}
              disabled={!hasChanges || updateProfile.isPending}
            >
              {updateProfile.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* HR Profile Link */}
      {hrEnabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hồ sơ Nhân sự</CardTitle>
            <CardDescription>
              Xem thông tin nhân sự đầy đủ của bạn
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SafeNavLink to={`/employee/${user?.id}?from=profile`}>
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <FileUser className="h-4 w-4" />
                  Xem Hồ sơ HR
                </span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </SafeNavLink>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
